/**
 * TanStack Query hooks — the only place the console talks to the API.
 *
 * Polling is explicit and bounded: jobs and pushes poll while they are in a
 * non-terminal state and stop as soon as they settle (`live_paused`, `reversed`,
 * `failed`), because a console that hammers the API burns the same operation quota the
 * push needs (02-platform-constraints.md: reporting draws on the same budget).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  Blueprint,
  BlueprintEditRequest,
  GenerateRequest,
  IngestRequest,
  JobStatus,
  Platform,
  ProjectCreateRequest,
  PushRequest,
  PushStatusResponse,
  ResearchRequest,
  ReverseRequest,
} from '@/api/types'
import { isTerminal } from '@/lib/pushState'

const SECOND = 1_000

export const queryKeys = {
  adAccounts: (platform?: Platform) => ['ad-accounts', platform ?? 'all'] as const,
  opsBudget: () => ['ops-budget'] as const,
  project: (projectId: string) => ['project', projectId] as const,
  blueprints: (projectId: string) => ['blueprints', projectId] as const,
  blueprint: (blueprintId: string) => ['blueprint', blueprintId] as const,
  diff: (blueprintId: string, against?: string) =>
    ['blueprint-diff', blueprintId, against ?? 'previous'] as const,
  job: (jobId: string) => ['job', jobId] as const,
  push: (pushId: string) => ['push', pushId] as const,
  metrics: (projectId: string, start: string, end: string, level: string) =>
    ['metrics', projectId, start, end, level] as const,
  keywordIdeas: (projectId: string) => ['keyword-ideas', projectId] as const,
}

export function useAdAccounts(platform?: Platform) {
  return useQuery({
    queryKey: queryKeys.adAccounts(platform),
    queryFn: ({ signal }) => api.listAdAccounts(platform, signal),
  })
}

export function useOpsBudget() {
  return useQuery({
    queryKey: queryKeys.opsBudget(),
    queryFn: ({ signal }) => api.getOpsBudget(signal),
    staleTime: 10 * SECOND,
  })
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: queryKeys.project(projectId),
    queryFn: ({ signal }) => api.getProject(projectId, signal),
    enabled: projectId !== '',
  })
}

export function useBlueprints(projectId: string) {
  return useQuery({
    queryKey: queryKeys.blueprints(projectId),
    queryFn: ({ signal }) => api.listBlueprints(projectId, signal),
    enabled: projectId !== '',
  })
}

export function useBlueprint(blueprintId: string) {
  return useQuery({
    queryKey: queryKeys.blueprint(blueprintId),
    queryFn: ({ signal }) => api.getBlueprint(blueprintId, signal),
    enabled: blueprintId !== '',
  })
}

export function useBlueprintDiff(blueprintId: string, against?: string) {
  return useQuery({
    queryKey: queryKeys.diff(blueprintId, against),
    queryFn: ({ signal }) => api.diffBlueprint(blueprintId, against, signal),
    enabled: blueprintId !== '',
  })
}

const JOB_TERMINAL: JobStatus['status'][] = ['succeeded', 'failed', 'canceled']

export function useJob(
  jobId: string | null,
  options: Partial<UseQueryOptions<JobStatus, Error, JobStatus, ReturnType<typeof queryKeys.job>>> = {},
) {
  return useQuery({
    queryKey: queryKeys.job(jobId ?? ''),
    queryFn: ({ signal }) => api.getJob(jobId!, signal),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 700
      return JOB_TERMINAL.includes(data.status) ? false : 700
    },
    ...options,
  })
}

/** Polls a push until it reaches a terminal state. Never invents a state locally. */
export function usePush(pushId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.push(pushId ?? ''),
    queryFn: ({ signal }) => api.getPush(pushId!, signal),
    enabled: Boolean(pushId) && enabled,
    refetchInterval: (query) => {
      const data: PushStatusResponse | undefined = query.state.data
      if (!data) return 600
      return isTerminal(data.state) ? false : 600
    },
  })
}

export function useMetrics(
  projectId: string,
  query: { start_date: string; end_date: string; level: 'campaign' | 'ad_group' | 'keyword' },
) {
  return useQuery({
    queryKey: queryKeys.metrics(projectId, query.start_date, query.end_date, query.level),
    queryFn: ({ signal }) => api.getMetrics(projectId, query, signal),
    enabled: projectId !== '',
    staleTime: 30 * SECOND,
  })
}

export function useKeywordIdeas(projectId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.keywordIdeas(projectId),
    queryFn: ({ signal }) => api.listKeywordIdeas(projectId, signal),
    enabled: projectId !== '' && enabled,
  })
}

// --- Mutations --------------------------------------------------------------

export function useCreateProject() {
  return useMutation({
    mutationFn: (body: ProjectCreateRequest) => api.createProject(body),
  })
}

/**
 * Ingest and generate are called *before* the UI has a project in state, so the project
 * id travels as a mutation variable rather than as a captured closure — otherwise the
 * first request goes to `/projects//ingest` while React catches up.
 */
export function useIngestSite() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body: IngestRequest }) =>
      api.ingestSite(projectId, body, crypto.randomUUID()),
    onSuccess: (_result, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.project(variables.projectId) })
    },
  })
}

export function useGenerateBlueprint() {
  return useMutation({
    mutationFn: ({ projectId, body }: { projectId: string; body: GenerateRequest }) =>
      api.generateBlueprint(projectId, body, crypto.randomUUID()),
  })
}

export function useStartResearch(projectId: string) {
  return useMutation({
    mutationFn: (body: ResearchRequest) => api.startResearch(projectId, body),
  })
}

export function useEditBlueprint(blueprintId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: BlueprintEditRequest) => api.editBlueprint(blueprintId, body, crypto.randomUUID()),
    onSuccess: (summary) => {
      void client.invalidateQueries({ queryKey: queryKeys.blueprints(summary.project_id) })
      void client.invalidateQueries({ queryKey: queryKeys.project(summary.project_id) })
    },
  })
}

export function useDryRun(blueprintId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (adAccountId: string) => api.dryRun(blueprintId, adAccountId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.blueprint(blueprintId) })
    },
  })
}

export function usePreflight(blueprintId: string) {
  return useMutation({
    mutationFn: (adAccountId: string) => api.preflight(blueprintId, adAccountId),
  })
}

export function useOpsEstimate(blueprintId: string) {
  return useMutation({
    mutationFn: (platform: Platform) => api.opsEstimate(blueprintId, platform),
  })
}

export function usePushBlueprint(blueprintId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ body }: { body: PushRequest; pushId?: string }) =>
      api.pushBlueprint(blueprintId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.opsBudget() })
    },
  })
}

export function useReversePush(pushId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: ReverseRequest) => api.reversePush(pushId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.push(pushId) })
    },
  })
}

export type { Blueprint }
